import type { BigNumber } from 'ethers'
import { useState, useCallback, useEffect } from 'react'
import { useWeb3 } from './useWeb3'
import { useTransactionDetails } from './useTransactionDetails'

import { toastError, toastInfo } from 'modules/toasts'
import { ResultTx } from '../types'
import { PopulatedTransaction } from '@ethersproject/contracts'
import { useBlockExplorerOpener } from './useBlockExplorerOpener'
import { useWeb3Signer } from 'modules/wallet/hooks/useWeb3Signer'
import { useConnectedWalletName } from 'modules/wallet/hooks/useConnectedWalletName'
import { noSigner } from 'shared/constants/errors'

export type PopulateMode = 'estimate' | 'submit'
export type PopulateArgs = {
  mode: PopulateMode
}
export type PopulateFn =
  | ((args: PopulateArgs) => PopulatedTransaction)
  | ((args: PopulateArgs) => Promise<PopulatedTransaction>)

export function useTransactionSender(populateTx: PopulateFn) {
  const { library, walletAddress } = useWeb3()
  const [isSigning, setSigning] = useState(false)
  const [resultTx, setResultTx] = useState<ResultTx | null>(null)
  const open = useBlockExplorerOpener(resultTx?.hash, 'tx')
  const { signer } = useWeb3Signer()
  const connectedWalletName = useConnectedWalletName()

  useEffect(() => {
    if (isSigning) setSigning(false)
    if (resultTx) setResultTx(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress])

  const { txStatus } = useTransactionDetails({
    hash: resultTx?.hash,
  })
  const [estimatedGas, setEstimatedGas] = useState<BigNumber | null>(null)

  const estimateGas = useCallback(async () => {
    const populatedTx = await populateTx({ mode: 'estimate' })
    const gasPrice = await library?.getGasPrice()
    if (!gasPrice || !library) {
      return null
    }
    try {
      const estimated = await library.estimateGas(populatedTx)
      const res = estimated.mul(gasPrice)
      setEstimatedGas(res)
      return res
    } catch (err) {
      console.error(err)
      setEstimatedGas(populatedTx.gasLimit?.mul(gasPrice) || null)
      return null
    }
  }, [library, populateTx])

  const resetGas = useCallback(() => {
    setEstimatedGas(null)
  }, [])

  const send = useCallback(async () => {
    if (!signer) {
      throw new Error(noSigner)
    }

    try {
      setResultTx(null)
      setSigning(true)

      const populatedTx = await populateTx({ mode: 'submit' })

      toastInfo(`Confirm transaction with ${connectedWalletName}`)
      const res = await signer.sendTransaction(populatedTx)

      setSigning(false)
      setResultTx(res)

      return res
    } catch (error: any) {
      setSigning(false)
      console.error(error)
      toastError(error.message || (error as any).toString())
      throw error
    }
  }, [signer, connectedWalletName, populateTx, setSigning, setResultTx])

  return {
    tx: resultTx,
    send,
    open,
    isSigning,
    estimateGas,
    estimatedGas,
    resetGas,
    ...txStatus,
  }
}
